/**
 * Championnat Individuel Attaque (CIA)
 *
 * Layout type Tables/Chat : colonne gauche (phases + divisions), zone droite (onglets).
 * Résultats : tableau croisé (scores entre joueurs du groupe).
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import ClickablePseudo from '../../components/ClickablePseudo';
import { DivisionForumPanel, ciaPhaseGeneralForumBucketId } from '../../components/competitions/DivisionForumPanel';
import MasterDetail from '../../components/MasterDetail';
import { useResponsive } from '../../hooks/useResponsive';
import type { Session } from '../../types/app';
import { useApp } from '../../contexts/AppContext';
import { BACKEND_URL } from '../../config';
import { formatFullDate } from '../../utils/forumDates';
import CiaRdvModal from '../../components/competitions/CiaRdvModal';
import { Switch } from '../../components/Switch';
import {
  buildGameHistoryFromReplayTable,
  buildReplaySeatLabels,
  gameHistoryToTrainingGameData,
  type CompareReplayDealEntry,
  type ReplayDonneListItem,
  type ReplayTable,
} from '../../utils/competitionReplay';

// ============================================
// Types
// ============================================

interface Phase {
  id: number;
  nom: string;
  etat: string;
  debut: string;
  fin: string;
  mode?: string;
}

interface Division {
  id: number;
  nom: string;
  type: string;
  index: number;
  hierarchie: number;
  nb_participants: number;
  nb_matches_played: number;
}

interface StandingItem {
  id: number;
  pseudo: string;
  pseudo_display: string;
  classement: number;
  points: number;
  point_average_general: number;
  joues: number;
  victoires: number;
  nuls: number;
  defaites: number;
  forfaits: number;
  penalites: number;
  resultat: string;
}

interface ResultItem {
  id: number;
  match_id: number | null;
  joueur1: {
    id: number;
    pseudo: string;
    pseudo_display: string;
    pm: number | null;
    table_idx: number | null;
  };
  joueur2: {
    id: number;
    pseudo: string;
    pseudo_display: string;
    pm: number | null;
    table_idx: number | null;
  };
  etat: string;
  type: string;
  date: string | null;
}

interface ParticipantAgenda {
  id: number;
  rdv: string;
  etat: string;
  is_proposer: boolean;
}

interface ParticipantItem {
  id: number;
  pseudo: string;
  pseudo_display: string;
  resultat: string;
  is_me: boolean;
  can_challenge: boolean;
  match_status: string | null;
  agenda?: ParticipantAgenda | null;
}

interface PhaseRosterItem {
  id: number;
  pseudo: string;
  pseudo_display: string;
  division_id: number;
  division_nom: string;
  resultat: string;
}

interface CIAOverview {
  competition: { id: number; nom: string; email?: string } | null;
  current_phase: Phase | null;
  phases: Phase[];
  organizers: { pseudo: string; pseudo_display: string }[];
}

type TabId = 'standings' | 'results' | 'composition' | 'memo' | 'forum';

type MemoSortKey = 'rdv' | 'datecreation' | 'etat' | 'auteur' | 'destinataire';

interface MemoRow {
  id: number;
  demandeur_pseudo: string;
  demandeur_display: string;
  adversaire_pseudo: string;
  adversaire_display: string;
  rdv: string;
  etat: string;
  datecreation: string;
  datereponse: string | null;
  match_etat: string | null;
  actif?: boolean;
}

type GeneralTabId = 'composition' | 'forum';

type SidebarSelection = 'general' | number;

interface CIAPageProps {
  session: Session;
}

// ============================================
// Helpers
// ============================================

function phaseStateLabel(etat: string): string {
  switch (etat) {
    case 'encours': return 'En cours';
    case 'barrage': return 'Barrages';
    case 'fini': return 'Terminée';
    case 'preparation': return 'Préparation';
    default: return etat;
  }
}

function resultatIcon(r: string): string {
  switch (r) {
    case 'montee': return '\u2191';
    case 'descente': return '\u2193';
    case 'abandon': return '\u2717';
    default: return '';
  }
}

function fmtPm(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return (Math.round(Number(v) * 1000) / 1000).toFixed(3);
}

function normPseudo(p: string): string {
  return p.toLowerCase();
}

async function ciaApiPost(
  path: string,
  pseudo: string,
  body: Record<string, unknown> = {},
): Promise<unknown> {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-Pseudo': pseudo,
    },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({ success: false, error: res.statusText }));
  if (!res.ok || !json.success) {
    throw new Error(
      typeof json.error === 'string'
        ? json.error
        : typeof json.details === 'string'
          ? json.details
          : 'Erreur',
    );
  }
  return json.data;
}

function matchStatusLabel(status: string | null): string | null {
  switch (status) {
    case 'match_already_played': return 'Défi joué';
    case 'player_abandoned': return 'Abandon';
    case 'different_divisions': return 'Autre poule';
    case 'participant_not_found': return 'Non inscrit';
    default: return null;
  }
}

function agendaEtatShort(etat: string): string {
  switch (etat) {
    case 'attente': return 'En attente';
    case 'ok': return 'RDV confirmé';
    case 'refus': return 'Refusé';
    case 'annule_auteur':
    case 'annule_destinataire':
    case 'annule_bot':
      return 'Annulé';
    default: return etat;
  }
}

/** Index des paires de résultats : clé ordonnée pour retrouver un match */
function buildResultPairMap(items: ResultItem[]): Map<string, ResultItem> {
  const m = new Map<string, ResultItem>();
  for (const it of items) {
    const a = normPseudo(it.joueur1.pseudo);
    const b = normPseudo(it.joueur2.pseudo);
    const key = a < b ? `${a}\0${b}` : `${b}\0${a}`;
    m.set(key, it);
  }
  return m;
}

function viewerTableIdx(result: ResultItem, viewerPseudo: string): number | null {
  const me = normPseudo(viewerPseudo);
  if (normPseudo(result.joueur1.pseudo) === me) return result.joueur1.table_idx;
  if (normPseudo(result.joueur2.pseudo) === me) return result.joueur2.table_idx;
  return null;
}

function canReplayResult(result: ResultItem): boolean {
  return result.etat === 'joue' && !!result.match_id;
}

function viewerIsInCiaMatch(result: ResultItem, viewerPseudo: string): boolean {
  const me = normPseudo(viewerPseudo);
  return normPseudo(result.joueur1.pseudo) === me || normPseudo(result.joueur2.pseudo) === me;
}

function ciaOpponentDisplay(result: ResultItem, viewerPseudo: string): string | undefined {
  const me = normPseudo(viewerPseudo);
  if (normPseudo(result.joueur1.pseudo) === me) return result.joueur2.pseudo_display;
  if (normPseudo(result.joueur2.pseudo) === me) return result.joueur1.pseudo_display;
  return undefined;
}

function ciaMatchTitle(
  result: ResultItem,
  viewerPseudo: string,
  fallbackOpponent?: string,
): string {
  const opponent = ciaOpponentDisplay(result, viewerPseudo) ?? fallbackOpponent;
  if (opponent && viewerIsInCiaMatch(result, viewerPseudo)) {
    return `Défi CIA — vs ${opponent}`;
  }
  return `Défi CIA — ${result.joueur1.pseudo_display} vs ${result.joueur2.pseudo_display}`;
}

function buildCiaHistoryContext(
  result: ResultItem,
  viewerPseudo: string,
  opponentDisplay?: string,
  extras?: { matchId?: number | null; dealNum?: number; tableIdx?: number | null },
) {
  return {
    kind: 'cia' as const,
    cia: {
      player1Display: result.joueur1.pseudo_display,
      player2Display: result.joueur2.pseudo_display,
      player1Pseudo: result.joueur1.pseudo,
      player2Pseudo: result.joueur2.pseudo,
      viewerPseudo,
      opponentDisplay: viewerIsInCiaMatch(result, viewerPseudo)
        ? (ciaOpponentDisplay(result, viewerPseudo) ?? opponentDisplay)
        : undefined,
      matchId: extras?.matchId ?? undefined,
      dealNum: extras?.dealNum,
      tableIdx: extras?.tableIdx ?? undefined,
    },
  };
}

// ============================================
// Cross-tab matrix
// ============================================

function CrossTabResultsFixed({
  standings,
  results,
  viewerPseudo,
  onReplayRequest,
}: {
  standings: StandingItem[];
  results: ResultItem[];
  viewerPseudo?: string;
  onReplayRequest?: (result: ResultItem, opponentDisplay?: string) => void;
}) {
  const players = useMemo(
    () => [...standings].sort((a, b) => a.classement - b.classement),
    [standings],
  );
  const pairMap = useMemo(() => buildResultPairMap(results), [results]);
  const me = viewerPseudo ? normPseudo(viewerPseudo) : '';

  if (players.length === 0) {
    return <div className="compet-empty">Aucun joueur dans ce groupe pour afficher la matrice</div>;
  }

  function cellContent(rowPseudo: string, colPseudo: string): React.ReactNode {
    if (normPseudo(rowPseudo) === normPseudo(colPseudo)) {
      return <span className="cia-matrix-diag-mark">—</span>;
    }
    const a = normPseudo(rowPseudo);
    const b = normPseudo(colPseudo);
    const key = a < b ? `${a}\0${b}` : `${b}\0${a}`;
    const r = pairMap.get(key);
    if (!r) return null;

    if (r.etat.startsWith('forfait')) {
      return <span className="cia-matrix-forfeit">Forfait</span>;
    }

    const rowIsJ1 = normPseudo(r.joueur1.pseudo) === normPseudo(rowPseudo);
    const rowPm = rowIsJ1 ? r.joueur1.pm : r.joueur2.pm;
    const colPm = rowIsJ1 ? r.joueur2.pm : r.joueur1.pm;
    return (
      <div className="cia-matrix-scores">
        <span>{fmtPm(rowPm)}</span>
        <span className="cia-matrix-sep">—</span>
        <span>{fmtPm(colPm)}</span>
      </div>
    );
  }

  return (
    <div className="cia-matrix-wrap">
      <table className="cia-matrix-table">
        <thead>
          <tr>
            <th className="cia-matrix-corner" />
            {players.map((col) => {
              const colIsMe = me !== '' && normPseudo(col.pseudo) === me;
              return (
                <th key={col.pseudo} className={`cia-matrix-col-head${colIsMe ? ' is-me' : ''}`}>
                  <div className="cia-matrix-col-inner">
                    <PlayerAvatar pseudo={col.pseudo} size={22} />
                    <span className="cia-matrix-col-name" title={col.pseudo_display}>
                      {col.pseudo_display}
                    </span>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {players.map((row) => {
            const rowIsMe = me !== '' && normPseudo(row.pseudo) === me;
            return (
            <tr key={row.pseudo} className={rowIsMe ? 'is-me' : undefined}>
              <th className={`cia-matrix-row-head${rowIsMe ? ' is-me' : ''}`}>
                <div className="cia-matrix-row-head-inner">
                  <PlayerAvatar pseudo={row.pseudo} size={24} />
                  <div className="cia-matrix-row-names">
                    <ClickablePseudo pseudo={row.pseudo}>
                      <span className="cia-matrix-row-pseudo">{row.pseudo_display}</span>
                    </ClickablePseudo>
                  </div>
                </div>
              </th>
              {players.map((col) => {
                const a = normPseudo(row.pseudo);
                const b = normPseudo(col.pseudo);
                const colIsMe = me !== '' && b === me;
                const pairKey = a < b ? `${a}\0${b}` : `${b}\0${a}`;
                const matchResult = a !== b ? pairMap.get(pairKey) : undefined;
                const hasMatch = Boolean(matchResult);
                const replayable =
                  hasMatch &&
                  matchResult &&
                  viewerPseudo &&
                  canReplayResult(matchResult);
                const tdClass = [
                  'cia-matrix-cell',
                  a === b ? 'cia-matrix-diag' : '',
                  a !== b && !hasMatch ? 'cia-matrix-empty' : '',
                  colIsMe ? 'cia-matrix-cell--mine-col' : '',
                  replayable ? 'cia-matrix-cell--replayable' : '',
                ].filter(Boolean).join(' ');
                const replayOpponent = rowIsMe ? col.pseudo_display : undefined;
                return (
                  <td
                    key={col.pseudo}
                    className={tdClass}
                    role={replayable ? 'button' : undefined}
                    tabIndex={replayable ? 0 : undefined}
                    title={replayable ? 'Voir les donnes jouées en entraînement' : undefined}
                    onClick={
                      replayable && matchResult && onReplayRequest
                        ? () => onReplayRequest(matchResult, replayOpponent)
                        : undefined
                    }
                    onKeyDown={
                      replayable && matchResult && onReplayRequest
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onReplayRequest(matchResult, replayOpponent);
                            }
                          }
                        : undefined
                    }
                  >
                    {cellContent(row.pseudo, col.pseudo)}
                  </td>
                );
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================
// Replay modal (choix de donne → entraînement)
// ============================================

interface CiaReplayModalProps {
  result: ResultItem;
  opponentDisplay?: string;
  viewerPseudo: string;
  onClose: () => void;
}

function ciaTablePlayerLabels(result: ResultItem): Map<number, string> {
  const m = new Map<number, string>();
  if (result.joueur1.table_idx != null) {
    m.set(result.joueur1.table_idx, result.joueur1.pseudo_display);
  }
  if (result.joueur2.table_idx != null) {
    m.set(result.joueur2.table_idx, result.joueur2.pseudo_display);
  }
  return m;
}

function CiaMatchReplayModal({ result, opponentDisplay, viewerPseudo, onClose }: CiaReplayModalProps) {
  const { startTraining } = useApp();
  const [donnes, setDonnes] = useState<ReplayDonneListItem[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDonne, setLoadingDonne] = useState<number | 'all' | null>(null);
  const [loadProgress, setLoadProgress] = useState<{ current: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const tableIdx = viewerTableIdx(result, viewerPseudo);
  const effectiveTableIdx = tableIdx ?? result.joueur1.table_idx ?? 1;
  const matchTitle = ciaMatchTitle(result, viewerPseudo, opponentDisplay);
  const matchSubtitle = viewerIsInCiaMatch(result, viewerPseudo)
    ? `Contre ${ciaOpponentDisplay(result, viewerPseudo) ?? opponentDisplay ?? '…'}`
    : `${result.joueur1.pseudo_display} vs ${result.joueur2.pseudo_display}`;

  useEffect(() => {
    if (!result.match_id) {
      setError('Match introuvable');
      setLoadingList(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/competition/replay/${result.match_id}`);
        const json = await res.json();
        if (!json.success) throw new Error(apiErrorMessage(json));
        if (cancelled) return;
        setDonnes(json.data?.donnes ?? []);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erreur de chargement');
        }
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [result.match_id]);

  const openAllInTraining = useCallback(async (selectedNumdonne?: number) => {
    if (!result.match_id || donnes.length === 0) return;
    setLoadingDonne(selectedNumdonne ?? 'all');
    setLoadProgress({ current: 0, total: donnes.length });
    setError(null);
    const playerByTable = ciaTablePlayerLabels(result);
    try {
      const dealsOut: CompareReplayDealEntry[] = await Promise.all(
        donnes.map(async (d) => {
          const res = await fetch(
            `${BACKEND_URL}/api/competition/replay/${result.match_id}/${d.numdonne}`,
          );
          const json = await res.json();
          if (!json.success) throw new Error(apiErrorMessage(json));

          const tables = ([...(json.data?.tables ?? [])] as ReplayTable[]).sort(
            (a, b) => Number(a.numtable) - Number(b.numtable),
          );
          if (tables.length === 0) throw new Error(`Donne ${d.numdonne} : aucune table`);

          const variants = tables.map((table) => {
            const tIdx = Number(table.numtable);
            const playerLabel = playerByTable.get(tIdx) ?? `Table ${tIdx}`;
            const tableScore = tIdx === 1 ? d.score_table1 : d.score_table2;
            const scoreText = tableScore != null ? fmtPm(tableScore) : '—';
            const history = buildGameHistoryFromReplayTable(table, viewerPseudo);
            return {
              tableIdx: tIdx,
              label: `${playerLabel} (${scoreText})`,
              scoreText,
              playerLabel,
              // Mains propres à chaque table (chien/écart du preneur déjà ajustés).
              gameData: gameHistoryToTrainingGameData(history),
              replaySeatLabels: buildReplaySeatLabels(table),
            };
          });

          return {
            dealIndex: d.numdonne,
            label: `Donne ${d.numdonne}`,
            variants,
          };
        }),
      );

      dealsOut.sort((a, b) => a.dealIndex - b.dealIndex);

      const selectedIndex =
        selectedNumdonne != null
          ? Math.max(0, dealsOut.findIndex((deal) => deal.dealIndex === selectedNumdonne))
          : 0;

      const selectedDeal = dealsOut[selectedIndex];
      const selectedVariantIndex = selectedDeal?.variants
        ? Math.max(0, selectedDeal.variants.findIndex((v) => v.tableIdx === effectiveTableIdx))
        : 0;

      sessionStorage.setItem(
        'library_load_request',
        JSON.stringify({
          compareReplayBatch: {
            title: matchTitle,
            deals: dealsOut,
            selectedIndex,
            selectedVariantIndex,
          },
          mode: 'replay',
          position: 0,
          replayAllBots: true,
          historyContext: buildCiaHistoryContext(result, viewerPseudo, opponentDisplay, {
            matchId: result.match_id,
            dealNum: selectedNumdonne ?? selectedDeal?.dealIndex,
            tableIdx: effectiveTableIdx,
          }),
        }),
      );

      startTraining({
        sessionId: '',
        displayMode: 'cards_on_table',
        showTimeline: true,
        botConfig: { disabledBots: new Set() },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoadingDonne(null);
      setLoadProgress(null);
    }
  }, [
    result.match_id,
    effectiveTableIdx,
    donnes,
    result,
    viewerPseudo,
    opponentDisplay,
    matchTitle,
    startTraining,
    onClose,
  ]);

  const openDonneInTraining = useCallback(
    (numdonne: number) => openAllInTraining(numdonne),
    [openAllInTraining],
  );

  return (
    <div className="cia-replay-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cia-replay-modal"
        role="dialog"
        aria-labelledby="cia-replay-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cia-replay-modal-header">
          <h3 id="cia-replay-modal-title">Donnes du défi</h3>
          <p className="cia-replay-modal-sub">
            {matchSubtitle} — les deux tables duplicate sont chargées ; choisissez la table
            de chaque joueur dans la liste des donnes en entraînement
          </p>
          <button type="button" className="cia-replay-modal-close" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </div>
        <div className="cia-replay-modal-body">
          {loadingList && <div className="cia-replay-modal-loading">Chargement des donnes…</div>}
          {error && <div className="compet-error">{error}</div>}
          {!loadingList && !error && donnes.length === 0 && (
            <div className="compet-empty">Aucune donne enregistrée pour ce match</div>
          )}
          {!loadingList && donnes.length > 0 && (
            <>
              <button
                type="button"
                className="cia-replay-all-btn"
                disabled={loadingDonne !== null}
                onClick={() => openAllInTraining()}
              >
                {loadingDonne === 'all'
                  ? loadProgress
                    ? `Chargement ${loadProgress.current}/${loadProgress.total}…`
                    : 'Chargement…'
                  : `Ouvrir les ${donnes.length} donnes en entraînement`}
              </button>
            <ul className="cia-replay-donne-list">
              {donnes.map((d) => (
                <li key={d.numdonne}>
                  <button
                    type="button"
                    className="cia-replay-donne-btn"
                    disabled={loadingDonne !== null}
                    onClick={() => openDonneInTraining(d.numdonne)}
                  >
                    <span className="cia-replay-donne-num">Donne {d.numdonne}</span>
                    {loadingDonne !== null && (loadingDonne === d.numdonne || loadingDonne === 'all') ? (
                      <span className="cia-replay-donne-loading">
                        {loadProgress
                          ? `${loadProgress.current}/${loadProgress.total}`
                          : 'Chargement…'}
                      </span>
                    ) : (
                      <span className="cia-replay-donne-scores">
                        {d.score_table1 != null ? fmtPm(d.score_table1) : '—'}
                        {' / '}
                        {d.score_table2 != null ? fmtPm(d.score_table2) : '—'}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Sub-tabs : classement
// ============================================

function StandingsTab({ items, viewerPseudo }: { items: StandingItem[]; viewerPseudo?: string }) {
  if (items.length === 0) return <div className="compet-empty">Aucun classement disponible</div>;

  const me = viewerPseudo ? normPseudo(viewerPseudo) : '';

  return (
    <table className="compet-standings-table">
      <thead>
        <tr>
          <th className="rank-col">#</th>
          <th>Joueur</th>
          <th className="num-col">Pts</th>
          <th className="num-col">J</th>
          <th className="num-col">V</th>
          <th className="num-col">N</th>
          <th className="num-col">D</th>
          <th className="num-col">F</th>
          <th className="num-col">Pén.</th>
          <th className="num-col">PM</th>
          <th style={{ width: 30, textAlign: 'center' }} />
        </tr>
      </thead>
      <tbody>
        {items.map((item) => {
          const isMe = me !== '' && normPseudo(item.pseudo) === me;
          return (
          <tr key={item.id} className={isMe ? 'is-me' : undefined}>
            <td className="rank-col">{item.classement}</td>
            <td>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <PlayerAvatar pseudo={item.pseudo} size={26} />
                <ClickablePseudo pseudo={item.pseudo}>{item.pseudo_display}</ClickablePseudo>
                {isMe && (
                  <span className="cia-player-mine-badge" title="Votre ligne dans ce groupe">Vous</span>
                )}
              </div>
            </td>
            <td className="num-col">{item.points}</td>
            <td className="num-col">{item.joues}</td>
            <td className="num-col">{item.victoires}</td>
            <td className="num-col">{item.nuls}</td>
            <td className="num-col">{item.defaites}</td>
            <td className="num-col">{item.forfaits || '-'}</td>
            <td className="num-col">{item.penalites > 0 ? item.penalites : '-'}</td>
            <td className="num-col">
              {item.point_average_general != null
                ? (Math.round(item.point_average_general * 100) / 100).toFixed(2)
                : '-'}
            </td>
            <td style={{ textAlign: 'center' }}>{resultatIcon(item.resultat)}</td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PhaseCompositionTab({ items, viewerPseudo }: { items: PhaseRosterItem[]; viewerPseudo?: string }) {
  if (items.length === 0) return <div className="compet-empty">Aucun participant dans cette phase</div>;

  const me = viewerPseudo ? normPseudo(viewerPseudo) : '';

  return (
    <table className="compet-standings-table">
      <thead>
        <tr>
          <th>Joueur</th>
          <th>Groupe / division</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id}>
            <td>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <PlayerAvatar pseudo={item.pseudo} size={26} />
                <ClickablePseudo pseudo={item.pseudo}>{item.pseudo_display}</ClickablePseudo>
                {me && normPseudo(item.pseudo) === me && (
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--accent, #4a90d9)' }}>(vous)</span>
                )}
              </div>
            </td>
            <td>
              <span className="cia-phase-roster-division">{item.division_nom}</span>
              {item.resultat === 'abandon' && (
                <span style={{ fontSize: '0.75rem', color: 'var(--error-color, #d32f2f)', marginLeft: 8 }}>Abandon</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CompositionTab({
  items,
  canAct,
  actionBusy,
  onProposeRdv,
  onLaunchChallenge,
  onAgendaAction,
}: {
  items: ParticipantItem[];
  viewerPseudo: string;
  canAct: boolean;
  actionBusy: boolean;
  onProposeRdv: (item: ParticipantItem) => void;
  onLaunchChallenge: (item: ParticipantItem) => void;
  onAgendaAction: (agendaId: number, action: 'accept' | 'refuse' | 'cancel' | 'cancel-destinataire') => void;
}) {
  if (items.length === 0) return <div className="compet-empty">Aucun participant</div>;

  return (
    <div className="cia-composition-list">
      {items.map((item) => {
        const statusLabel = !item.is_me ? matchStatusLabel(item.match_status) : null;
        const showChallenge = canAct && !item.is_me && item.can_challenge;
        const showRdv = canAct && !item.is_me && item.can_challenge;
        const agenda = item.agenda;

        return (
          <div
            key={item.id}
            className={`cia-composition-row compet-result-item${item.is_me ? ' is-me' : ''}`}
            style={{ opacity: item.resultat === 'abandon' ? 0.5 : 1 }}
          >
            <div className="cia-composition-main">
              <PlayerAvatar pseudo={item.pseudo} size={28} />
              <ClickablePseudo pseudo={item.pseudo}>{item.pseudo_display}</ClickablePseudo>
              {item.is_me && (
                <span className="cia-player-mine-badge" title="Votre ligne dans ce groupe">Vous</span>
              )}
              {item.resultat === 'abandon' && (
                <span className="cia-composition-tag cia-composition-tag--bad">Abandon</span>
              )}
              {statusLabel && (
                <span className="cia-composition-tag">{statusLabel}</span>
              )}
            </div>

            {canAct && !item.is_me && (
              <div className="cia-composition-actions">
                {agenda && (agenda.etat === 'attente' || agenda.etat === 'ok') ? (
                  <div className="cia-composition-rdv">
                    <span className="cia-composition-rdv-date">
                      {agenda.etat === 'attente' ? 'Jouer ' : 'RDV '}
                      {formatFullDate(agenda.rdv)} ?
                    </span>
                    <span className="cia-composition-rdv-etat">{agendaEtatShort(agenda.etat)}</span>
                    <div className="cia-composition-rdv-buttons">
                      {agenda.etat === 'attente' && !agenda.is_proposer && (
                        <>
                          <button
                            type="button"
                            className="cia-btn cia-btn--small cia-btn--primary"
                            disabled={actionBusy}
                            onClick={() => onAgendaAction(agenda.id, 'accept')}
                          >
                            Accepter
                          </button>
                          <button
                            type="button"
                            className="cia-btn cia-btn--small"
                            disabled={actionBusy}
                            onClick={() => onAgendaAction(agenda.id, 'refuse')}
                          >
                            Refuser
                          </button>
                        </>
                      )}
                      {agenda.etat === 'attente' && agenda.is_proposer && (
                        <button
                          type="button"
                          className="cia-btn cia-btn--small"
                          disabled={actionBusy}
                          onClick={() => onAgendaAction(agenda.id, 'cancel')}
                        >
                          Annuler
                        </button>
                      )}
                      {agenda.etat === 'ok' && (
                        <button
                          type="button"
                          className="cia-btn cia-btn--small"
                          disabled={actionBusy}
                          onClick={() => onAgendaAction(
                            agenda.id,
                            agenda.is_proposer ? 'cancel' : 'cancel-destinataire',
                          )}
                        >
                          Annuler le RDV
                        </button>
                      )}
                    </div>
                  </div>
                ) : showRdv ? (
                  <button
                    type="button"
                    className="cia-btn cia-btn--small"
                    disabled={actionBusy}
                    onClick={() => onProposeRdv(item)}
                  >
                    Proposer un RDV
                  </button>
                ) : null}

                {showChallenge && (
                  <button
                    type="button"
                    className="cia-btn cia-btn--small cia-btn--primary"
                    disabled={actionBusy}
                    onClick={() => onLaunchChallenge(item)}
                  >
                    Lancer le défi
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Mémo (agenda RDV)
// ============================================

const CIA_MEMO_ETAT_LABEL: Record<string, string> = {
  ok: 'Rendez-vous accepté',
  attente: 'En attente de réponse',
  annule_destinataire: 'Annulé par le destinataire',
  annule_auteur: 'Annulé par le demandeur',
  refus: 'Refusé par le destinataire',
  annule_bot: 'Rendez-vous caduc',
};

const CIA_MEMO_INACTIVE_ETATS = new Set(['annule_bot', 'refus', 'annule_auteur', 'annule_destinataire']);

function isMemoInactive(row: MemoRow): boolean {
  return CIA_MEMO_INACTIVE_ETATS.has(row.etat);
}

function isMemoPast(row: MemoRow): boolean {
  if (row.match_etat) return true;
  if (row.actif === false) return true;
  const rdvTime = new Date(row.rdv).getTime();
  return !Number.isNaN(rdvTime) && rdvTime < Date.now();
}

function apiErrorMessage(json: { error?: string; details?: string | null }): string {
  if (typeof json.details === 'string' && json.details.length > 0) return json.details;
  return json.error || 'Erreur de chargement';
}

function memoStatusIconClass(etat: string): string {
  if (etat === 'ok') return 'cia-memo-icon cia-memo-icon--ok';
  if (etat === 'attente') return 'cia-memo-icon cia-memo-icon--wait';
  return 'cia-memo-icon cia-memo-icon--bad';
}

function memoMatchSubline(matchEtat: string | null): string | null {
  if (!matchEtat) return null;
  if (matchEtat === 'joue') return 'Défi joué';
  if (matchEtat === 'forfait1' || matchEtat === 'forfait2') return 'Forfait';
  return null;
}

function MemoTab({
  rows,
  sort,
  onSortChange,
  viewerPseudo,
  actionBusy,
  onAgendaAction,
}: {
  rows: MemoRow[];
  sort: { key: MemoSortKey; order: 'asc' | 'desc' };
  onSortChange: (key: MemoSortKey) => void;
  viewerPseudo: string;
  actionBusy: boolean;
  onAgendaAction: (agendaId: number, action: 'accept' | 'refuse' | 'cancel' | 'cancel-destinataire') => void;
}) {
  const [playerFilter, setPlayerFilter] = useState('');
  const [hideInactive, setHideInactive] = useState(false);
  const [hidePast, setHidePast] = useState(false);

  const sortMark = (key: MemoSortKey) =>
    sort.key === key ? (sort.order === 'asc' ? ' ↑' : ' ↓') : '';

  const playerOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) {
      map.set(row.demandeur_pseudo, row.demandeur_display);
      map.set(row.adversaire_pseudo, row.adversaire_display);
    }
    return [...map.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'fr'))
      .map(([pseudo, display]) => ({ pseudo, display }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (playerFilter) {
      const p = normPseudo(playerFilter);
      result = result.filter(
        (r) => normPseudo(r.demandeur_pseudo) === p || normPseudo(r.adversaire_pseudo) === p,
      );
    }
    if (hideInactive) {
      result = result.filter((r) => !isMemoInactive(r));
    }
    if (hidePast) {
      result = result.filter((r) => !isMemoPast(r));
    }
    return result;
  }, [rows, playerFilter, hideInactive, hidePast]);

  const filtersActive = playerFilter !== '' || hideInactive || hidePast;

  if (rows.length === 0) {
    return (
      <div className="cia-memo-empty compet-empty">
        Aucun rendez-vous enregistré pour cette poule
      </div>
    );
  }

  return (
    <div className="cia-memo-wrap">
      <div className="cia-memo-filters">
        <label className="cia-memo-filter">
          <span className="cia-memo-filter-label">Joueur</span>
          <select
            className="cia-memo-filter-select"
            value={playerFilter}
            onChange={(e) => setPlayerFilter(e.target.value)}
          >
            <option value="">Tous les joueurs</option>
            {playerOptions.map((p) => (
              <option key={p.pseudo} value={p.pseudo}>{p.display}</option>
            ))}
          </select>
        </label>
        <label className="cia-memo-filter cia-memo-filter--check">
          <span>Masquer caducs, refusés et annulés</span>
          <Switch compact checked={hideInactive} onChange={setHideInactive} />
        </label>
        <label className="cia-memo-filter cia-memo-filter--check">
          <span>Masquer les RDV passés</span>
          <Switch compact checked={hidePast} onChange={setHidePast} />
        </label>
        {filtersActive && (
          <span className="cia-memo-filter-count">
            {filteredRows.length} / {rows.length}
          </span>
        )}
      </div>
      {filteredRows.length === 0 ? (
        <div className="cia-memo-empty compet-empty">
          Aucun rendez-vous ne correspond aux filtres sélectionnés
        </div>
      ) : (
      <table className="cia-memo-table">
        <thead>
          <tr>
            <th>
              <button type="button" className="cia-memo-sort" onClick={() => onSortChange('auteur')}>
                Demandeur{sortMark('auteur')}
              </button>
            </th>
            <th>
              <button type="button" className="cia-memo-sort" onClick={() => onSortChange('destinataire')}>
                Adversaire{sortMark('destinataire')}
              </button>
            </th>
            <th>
              <button type="button" className="cia-memo-sort" onClick={() => onSortChange('rdv')}>
                Rendez-vous{sortMark('rdv')}
              </button>
            </th>
            <th className="cia-memo-col-icon" aria-hidden />
            <th>
              <button type="button" className="cia-memo-sort" onClick={() => onSortChange('etat')}>
                État{sortMark('etat')}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredRows.map((row) => {
            const sub = memoMatchSubline(row.match_etat);
            const etatLabel = CIA_MEMO_ETAT_LABEL[row.etat] || row.etat;
            const detail =
              row.etat !== 'attente' && row.etat !== 'annule_bot' && row.datereponse
                ? `${etatLabel} le ${formatFullDate(row.datereponse)}`
                : etatLabel;
            const me = normPseudo(viewerPseudo);
            const isProposer = normPseudo(row.demandeur_pseudo) === me;
            const isRecipient = normPseudo(row.adversaire_pseudo) === me;
            const involved = isProposer || isRecipient;
            const canActOnRow = involved && !isMemoInactive(row) && !isMemoPast(row);

            return (
              <tr key={row.id}>
                <td>
                  <div className="cia-memo-player">
                    <PlayerAvatar pseudo={row.demandeur_pseudo} size={28} />
                    <ClickablePseudo pseudo={row.demandeur_pseudo}>
                      <span className="cia-memo-pseudo">{row.demandeur_display}</span>
                    </ClickablePseudo>
                  </div>
                </td>
                <td>
                  <div className="cia-memo-player">
                    <PlayerAvatar pseudo={row.adversaire_pseudo} size={28} />
                    <ClickablePseudo pseudo={row.adversaire_pseudo}>
                      <span className="cia-memo-pseudo">{row.adversaire_display}</span>
                    </ClickablePseudo>
                  </div>
                </td>
                <td className="cia-memo-rdv-cell">
                  <div className="cia-memo-rdv-main">{formatFullDate(row.rdv)}</div>
                  {sub ? <div className="cia-memo-rdv-sub">{sub}</div> : null}
                </td>
                <td className="cia-memo-col-icon">
                  <span className={memoStatusIconClass(row.etat)} title={etatLabel} />
                </td>
                <td className="cia-memo-etat-cell">
                  <small>{detail}</small>
                  {canActOnRow && row.etat === 'attente' && isRecipient && (
                    <div className="cia-memo-actions">
                      <button type="button" className="cia-btn cia-btn--small cia-btn--primary" disabled={actionBusy} onClick={() => onAgendaAction(row.id, 'accept')}>Accepter</button>
                      <button type="button" className="cia-btn cia-btn--small" disabled={actionBusy} onClick={() => onAgendaAction(row.id, 'refuse')}>Refuser</button>
                    </div>
                  )}
                  {canActOnRow && row.etat === 'attente' && isProposer && (
                    <div className="cia-memo-actions">
                      <button type="button" className="cia-btn cia-btn--small" disabled={actionBusy} onClick={() => onAgendaAction(row.id, 'cancel')}>Annuler</button>
                    </div>
                  )}
                  {canActOnRow && row.etat === 'ok' && (
                    <div className="cia-memo-actions">
                      <button
                        type="button"
                        className="cia-btn cia-btn--small"
                        disabled={actionBusy}
                        onClick={() => onAgendaAction(row.id, isProposer ? 'cancel' : 'cancel-destinataire')}
                      >
                        Annuler le RDV
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      )}
    </div>
  );
}

// ============================================
// Page principale
// ============================================

export default function CIAPage({ session }: CIAPageProps) {
  const { isCompact } = useResponsive();
  const [showMobileDetail, setShowMobileDetail] = useState(false);
  const myDivisionButtonRef = useRef<HTMLButtonElement | null>(null);

  const [overview, setOverview] = useState<CIAOverview | null>(null);
  const [phases, setPhases] = useState<Phase[]>([]);
  const [selectedPhaseId, setSelectedPhaseId] = useState<number | null>(null);
  const [divisions, setDivisions] = useState<Division[]>([]);
  const [userDivisionIdInPhase, setUserDivisionIdInPhase] = useState<number | null>(null);
  /** Poule choisie, ou « Présentation générale » */
  const [sidebarSelection, setSidebarSelection] = useState<SidebarSelection | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('standings');
  const [generalTab, setGeneralTab] = useState<GeneralTabId>('composition');

  const [standings, setStandings] = useState<StandingItem[]>([]);
  const [results, setResults] = useState<ResultItem[]>([]);
  const [participants, setParticipants] = useState<ParticipantItem[]>([]);
  const [phaseRoster, setPhaseRoster] = useState<PhaseRosterItem[]>([]);
  const [memoRows, setMemoRows] = useState<MemoRow[]>([]);
  const [memoSort, setMemoSort] = useState<{ key: MemoSortKey; order: 'asc' | 'desc' }>({
    key: 'rdv',
    order: 'asc',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [replayModal, setReplayModal] = useState<{
    result: ResultItem;
    opponentDisplay?: string;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [rdvTarget, setRdvTarget] = useState<ParticipantItem | null>(null);

  const handleReplayRequest = useCallback((result: ResultItem, opponentDisplay?: string) => {
    setReplayModal({ result, opponentDisplay });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${BACKEND_URL}/api/rankings/competition/cia`);
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        const data = json.data as CIAOverview;
        setOverview(data);
        setPhases(data.phases || []);
        if (data.current_phase) setSelectedPhaseId(data.current_phase.id);
        else if (data.phases?.length) setSelectedPhaseId(data.phases[0].id);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedPhaseId) return;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/rankings/competition/cia/${selectedPhaseId}/divisions`);
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        const raw: Division[] = json.data?.items ?? [];
        const items = raw.filter((d) => (d.nb_participants ?? 0) > 0);

        let myDiv: number | null = null;
        try {
          const meRes = await fetch(
            `${BACKEND_URL}/api/competition/cia/me?phase_id=${selectedPhaseId}`,
            { headers: { 'X-User-Pseudo': session.pseudo } },
          );
          const meJson = await meRes.json();
          if (meJson.success && meJson.data?.participant?.division_id != null) {
            myDiv = Number(meJson.data.participant.division_id);
          }
        } catch {
          /* pas connecté ou erreur API me : pas de division « à moi » */
        }

        const myDivisionInList =
          myDiv != null && items.some((d) => d.id === myDiv) ? myDiv : null;

        setDivisions(items);
        setUserDivisionIdInPhase(myDivisionInList);

        if (items.length > 0) {
          setSidebarSelection(myDivisionInList ?? 'general');
        } else {
          setSidebarSelection(null);
          setShowMobileDetail(false);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement des divisions');
      }
    })();
  }, [selectedPhaseId, session.pseudo]);

  useLayoutEffect(() => {
    if (userDivisionIdInPhase == null || divisions.length === 0) return;
    if (sidebarSelection !== userDivisionIdInPhase) return;
    myDivisionButtonRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedPhaseId, divisions, userDivisionIdInPhase, sidebarSelection]);

  const selectedDivisionId =
    sidebarSelection !== null && sidebarSelection !== 'general' ? sidebarSelection : null;

  const isMyPoule =
    selectedDivisionId != null && userDivisionIdInPhase === selectedDivisionId;

  const loadMemo = useCallback(async () => {
    if (!selectedPhaseId || selectedDivisionId == null) return;
    const q = new URLSearchParams({
      sort: memoSort.key,
      order: memoSort.order,
    });
    const res = await fetch(
      `${BACKEND_URL}/api/competition/cia/${selectedPhaseId}/divisions/${selectedDivisionId}/memo?${q}`,
    );
    const json = await res.json();
    if (!json.success) throw new Error(json.error);
    setMemoRows(json.data?.items ?? []);
  }, [selectedPhaseId, selectedDivisionId, memoSort]);

  const refreshParticipants = useCallback(async () => {
    if (!selectedPhaseId || selectedDivisionId == null) return;
    const res = await fetch(
      `${BACKEND_URL}/api/competition/cia/${selectedPhaseId}/divisions/${selectedDivisionId}/participants`,
      { headers: { 'X-User-Pseudo': session.pseudo } },
    );
    const json = await res.json();
    if (!json.success) throw new Error(json.error);
    setParticipants(json.data?.items ?? []);
  }, [selectedPhaseId, selectedDivisionId, session.pseudo]);

  const refreshCiaActionViews = useCallback(async () => {
    const tasks: Promise<void>[] = [];
    if (activeTab === 'composition' || activeTab === 'memo') {
      tasks.push(refreshParticipants().catch(() => undefined));
    }
    if (activeTab === 'memo') {
      tasks.push(loadMemo().catch(() => undefined));
    }
    await Promise.all(tasks);
  }, [activeTab, refreshParticipants, loadMemo]);

  const handleAgendaAction = useCallback(async (
    agendaId: number,
    action: 'accept' | 'refuse' | 'cancel' | 'cancel-destinataire',
  ) => {
    setActionBusy(true);
    setActionMessage(null);
    try {
      await ciaApiPost(`/api/competition/cia/agenda/${agendaId}/${action}`, session.pseudo);
      const labels = {
        accept: 'Rendez-vous accepté',
        refuse: 'Proposition refusée',
        cancel: 'Rendez-vous annulé',
        'cancel-destinataire': 'Rendez-vous annulé',
      };
      setActionMessage(labels[action]);
      await refreshCiaActionViews();
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setActionBusy(false);
    }
  }, [session.pseudo, refreshCiaActionViews]);

  const handleSubmitRdv = useCallback(async (rdvIso: string) => {
    if (!selectedPhaseId || !rdvTarget) return;
    setActionBusy(true);
    setActionMessage(null);
    try {
      await ciaApiPost(
        `/api/competition/cia/${selectedPhaseId}/agenda`,
        session.pseudo,
        { adversaire: rdvTarget.pseudo, rdv: rdvIso },
      );
      setActionMessage(`Proposition envoyée à ${rdvTarget.pseudo_display}`);
      setRdvTarget(null);
      await refreshCiaActionViews();
    } catch (err) {
      throw err;
    } finally {
      setActionBusy(false);
    }
  }, [selectedPhaseId, rdvTarget, session.pseudo, refreshCiaActionViews]);

  const handleLaunchChallenge = useCallback(async (item: ParticipantItem) => {
    const ok = window.confirm(
      `Lancer un défi CIA contre ${item.pseudo_display} ?\n\n`
      + 'Le défi sera créé immédiatement. Les deux joueurs ont 10 minutes pour s\'y connecter sur Webtarot.',
    );
    if (!ok) return;
    setActionBusy(true);
    setActionMessage(null);
    try {
      await ciaApiPost('/api/competition/cia/challenge', session.pseudo, { adversaire: item.pseudo });
      setActionMessage(`Défi créé contre ${item.pseudo_display}. Connectez-vous sans délai sur Webtarot !`);
      await refreshParticipants();
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setActionBusy(false);
    }
  }, [session.pseudo, refreshParticipants]);

  const loadTabData = useCallback(async () => {
    if (!selectedPhaseId || sidebarSelection === null) return;

    if (sidebarSelection === 'general') {
      if (generalTab !== 'composition') return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/competition/cia/${selectedPhaseId}/roster`);
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        setPhaseRoster(json.data?.items ?? []);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      }
      return;
    }

    const divId = sidebarSelection;
    try {
      if (activeTab === 'standings') {
        const res = await fetch(
          `${BACKEND_URL}/api/rankings/competition/cia/${selectedPhaseId}/divisions/${divId}/standings`,
        );
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        setStandings(json.data?.items ?? []);
      } else if (activeTab === 'results') {
        const base = `${BACKEND_URL}/api/rankings/competition/cia/${selectedPhaseId}/divisions/${divId}`;
        const [resS, resR] = await Promise.all([
          fetch(`${base}/standings`),
          fetch(`${BACKEND_URL}/api/competition/cia/${selectedPhaseId}/divisions/${divId}/results?limit=2000`),
        ]);
        const jsonS = await resS.json();
        const jsonR = await resR.json();
        if (!jsonS.success) throw new Error(jsonS.error);
        if (!jsonR.success) throw new Error(jsonR.error);
        setStandings(jsonS.data?.items ?? []);
        setResults(jsonR.data?.items ?? []);
      } else if (activeTab === 'composition') {
        const res = await fetch(
          `${BACKEND_URL}/api/competition/cia/${selectedPhaseId}/divisions/${divId}/participants`,
          { headers: { 'X-User-Pseudo': session.pseudo } },
        );
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        setParticipants(json.data?.items ?? []);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    }
  }, [selectedPhaseId, sidebarSelection, activeTab, generalTab, session.pseudo]);

  useEffect(() => {
    if (sidebarSelection === 'general') {
      if (generalTab === 'forum') return;
      loadTabData();
      return;
    }
    if (sidebarSelection !== null && activeTab === 'forum') return;
    if (activeTab === 'memo') return;
    loadTabData();
  }, [loadTabData, activeTab, generalTab, sidebarSelection]);

  useEffect(() => {
    if (!selectedPhaseId || sidebarSelection === null || sidebarSelection === 'general') return;
    if (activeTab !== 'memo') return;
    (async () => {
      try {
        await loadMemo();
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      }
    })();
  }, [selectedPhaseId, sidebarSelection, activeTab, loadMemo]);

  const selectDivision = useCallback((id: number) => {
    setSidebarSelection(id);
    if (isCompact) setShowMobileDetail(true);
  }, [isCompact]);

  const selectGeneralPresentation = useCallback(() => {
    setSidebarSelection('general');
    if (isCompact) setShowMobileDetail(true);
  }, [isCompact]);

  const onPhaseChange = useCallback((id: number) => {
    setSelectedPhaseId(id);
    setShowMobileDetail(false);
  }, []);

  const sidebar = (
    <div className="cia-sidebar">
      <div className="cia-sidebar-title">CIA</div>
      <label className="cia-sidebar-label">Phase</label>
      <select
        className="cia-sidebar-select"
        value={selectedPhaseId ?? ''}
        onChange={(e) => onPhaseChange(Number(e.target.value))}
      >
        {phases.map((p) => (
          <option key={p.id} value={p.id}>
            {p.nom} ({phaseStateLabel(p.etat)})
          </option>
        ))}
      </select>
      <label className="cia-sidebar-label">Division / groupe</label>
      <div className="cia-sidebar-divisions">
        {divisions.length === 0 ? (
          <div className="compet-empty" style={{ padding: '12px 0' }}>Aucune division active</div>
        ) : (
          <>
            <button
              type="button"
              className={[
                'cia-division-item',
                'cia-division-item--general',
                sidebarSelection === 'general' ? 'active' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={selectGeneralPresentation}
            >
              <span className="cia-division-name">Présentation générale</span>
            </button>
            {divisions.map((d) => {
              const isMyDivision = userDivisionIdInPhase === d.id;
              return (
                <button
                  key={d.id}
                  ref={isMyDivision ? myDivisionButtonRef : undefined}
                  type="button"
                  className={[
                    'cia-division-item',
                    sidebarSelection === d.id ? 'active' : '',
                    isMyDivision ? 'cia-division-item--mine' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => selectDivision(d.id)}
                >
                  <span className="cia-division-item-inner">
                    <span className="cia-division-name">{d.nom}</span>
                    {isMyDivision && (
                      <span className="cia-division-mine-badge" title="Votre groupe pour cette phase">
                        Ma poule
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </>
        )}
      </div>
    </div>
  );

  const detailTitle =
    sidebarSelection === 'general'
      ? 'Présentation générale'
      : selectedDivisionId != null
        ? divisions.find((d) => d.id === selectedDivisionId)?.nom ?? 'CIA'
        : 'CIA';

  const detailContent =
    sidebarSelection === null ? (
      <div className="compet-empty">Sélectionnez une entrée dans la colonne de gauche</div>
    ) : sidebarSelection === 'general' ? (
      <>
        <div className="compet-tabs">
          <button
            type="button"
            className={`compet-tab ${generalTab === 'composition' ? 'active' : ''}`}
            onClick={() => setGeneralTab('composition')}
          >
            Composition
          </button>
          <button
            type="button"
            className={`compet-tab ${generalTab === 'forum' ? 'active' : ''}`}
            onClick={() => setGeneralTab('forum')}
          >
            Forum général
          </button>
        </div>
        <div
          className={[
            'compet-content',
            'cia-detail-content',
            generalTab === 'forum' ? 'cia-detail-content--forum' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {error && overview && generalTab === 'composition' && (
            <div className="compet-error" style={{ marginBottom: 8 }}>{error}</div>
          )}
          {generalTab === 'composition' && (
            <PhaseCompositionTab items={phaseRoster} viewerPseudo={session.pseudo} />
          )}
          {generalTab === 'forum' && selectedPhaseId != null && (
            <DivisionForumPanel
              divisionId={0}
              phaseId={selectedPhaseId}
              forumBucketOverride={ciaPhaseGeneralForumBucketId(selectedPhaseId)}
              viewerPseudo={session.pseudo}
              canPost={userDivisionIdInPhase !== null}
            />
          )}
        </div>
      </>
    ) : (
      <>
        <div className="compet-tabs">
          <button type="button" className={`compet-tab ${activeTab === 'standings' ? 'active' : ''}`} onClick={() => setActiveTab('standings')}>Classement</button>
          <button type="button" className={`compet-tab ${activeTab === 'results' ? 'active' : ''}`} onClick={() => setActiveTab('results')}>Résultats</button>
          <button type="button" className={`compet-tab ${activeTab === 'composition' ? 'active' : ''}`} onClick={() => setActiveTab('composition')}>Composition</button>
          <button type="button" className={`compet-tab ${activeTab === 'memo' ? 'active' : ''}`} onClick={() => setActiveTab('memo')}>Mémo</button>
          <button type="button" className={`compet-tab ${activeTab === 'forum' ? 'active' : ''}`} onClick={() => setActiveTab('forum')}>Forum</button>
        </div>
        <div
          className={[
            'compet-content',
            'cia-detail-content',
            activeTab === 'forum'
              ? 'cia-detail-content--forum'
              : activeTab === 'results'
                ? 'cia-detail-content--results'
                : activeTab === 'memo'
                  ? 'cia-detail-content--memo'
                  : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {error && overview && <div className="compet-error" style={{ marginBottom: 8 }}>{error}</div>}
          {actionMessage && (
            <div className="cia-action-banner" role="status">{actionMessage}</div>
          )}
          {activeTab === 'standings' && <StandingsTab items={standings} viewerPseudo={session.pseudo} />}
          {activeTab === 'results' && (
            <CrossTabResultsFixed
              standings={standings}
              results={results}
              viewerPseudo={session.pseudo}
              onReplayRequest={handleReplayRequest}
            />
          )}
          {activeTab === 'composition' && (
            <CompositionTab
              items={participants}
              viewerPseudo={session.pseudo}
              canAct={isMyPoule}
              actionBusy={actionBusy}
              onProposeRdv={setRdvTarget}
              onLaunchChallenge={handleLaunchChallenge}
              onAgendaAction={handleAgendaAction}
            />
          )}
          {activeTab === 'memo' && (
            <MemoTab
              key={selectedDivisionId ?? 'memo'}
              rows={memoRows}
              sort={memoSort}
              viewerPseudo={session.pseudo}
              actionBusy={actionBusy}
              onAgendaAction={handleAgendaAction}
              onSortChange={(key) => {
                setMemoSort((prev) =>
                  prev.key === key
                    ? { key, order: prev.order === 'asc' ? 'desc' : 'asc' }
                    : { key, order: key === 'rdv' ? 'asc' : 'desc' },
                );
              }}
            />
          )}
          {activeTab === 'forum' && selectedPhaseId != null && selectedDivisionId != null && (
            <DivisionForumPanel
              divisionId={selectedDivisionId}
              phaseId={selectedPhaseId}
              viewerPseudo={session.pseudo}
              canPost={
                userDivisionIdInPhase !== null &&
                selectedDivisionId === userDivisionIdInPhase
              }
            />
          )}
        </div>
      </>
    );

  if (loading && !overview) return <div className="compet-loading">Chargement...</div>;
  if (error && !overview) return <div className="compet-error">{error}</div>;

  return (
    <div className="chat-container cia-page-root">
      <MasterDetail
        master={sidebar}
        detail={detailContent}
        detailTitle={detailTitle}
        hasSelection={isCompact && showMobileDetail && sidebarSelection !== null}
        onBack={() => setShowMobileDetail(false)}
      />
      {replayModal && (
        <CiaMatchReplayModal
          result={replayModal.result}
          opponentDisplay={replayModal.opponentDisplay}
          viewerPseudo={session.pseudo}
          onClose={() => setReplayModal(null)}
        />
      )}
      {rdvTarget && (
        <CiaRdvModal
          opponentDisplay={rdvTarget.pseudo_display}
          onClose={() => setRdvTarget(null)}
          onSubmit={handleSubmitRdv}
        />
      )}
    </div>
  );
}
